/**
 * Sending a note to the other staff of the part it is already in.
 *
 * A keyboard part is written on two staves and played with two hands, and the two do not
 * agree: the left hand reaches up into the treble for a bar, an arpeggio runs from the
 * bottom of the bass to the top of the treble without pausing at the line between them.
 * Notation answers that by drawing a note on one staff while it still belongs to the voice
 * on the other -- the beam crosses the gap and the run is read as the one gesture it is.
 *
 * So this layer moves where a note is *drawn* and nothing else. The voice keeps it: it is
 * still counted in that line's bar, still beamed with its neighbours, still played at the
 * same moment by the same hand. Everything that would change if the note were moved into
 * the other staff's voice -- its rhythm against the other voice, which rests fill the bar,
 * what a part extraction takes -- is exactly what must not change here.
 *
 * MusicXML says this the same way, which is why it survives a round trip: `<voice>` stays
 * where it was and `<staff>` moves. Nothing about the sound changes at all.
 */

import type { Command } from "../commands/types";
import type { EventLocation } from "../document/query";
import type { Score } from "../document/types";
import { focusLocation, selectedLocations } from "./selection";
import type { Selection } from "./selection";

/** The part and the voice's own staff for a located event, or `null` if either is gone. */
function homeOf(
  score: Score,
  at: EventLocation,
): { readonly staves: number; readonly home: number; readonly ossia: boolean } | null {
  const part = score.parts.find((candidate) => candidate.id === at.partId);
  const measure = part?.measures[at.measureId];
  const voice = measure?.voices[at.voiceIndex];
  if (part === undefined || voice === undefined) return null;
  return { staves: part.staves.length, home: voice.staff, ossia: voice.ossia };
}

/** Which staff an event is drawn on: its own if it was sent to one, else its voice's. */
function drawnOn(at: EventLocation, home: number): number {
  return at.event.staff ?? home;
}

/**
 * Send the selection a staff further down the part, or up when `delta` is negative.
 *
 * Down is a larger staff index, as the staves are ordered on the page: the right hand's
 * staff is first and the left hand's is second, so a note sent down goes into the bass.
 * Every selected note moves by the same step rather than to a shared staff, which keeps
 * the shape of a passage that was already split across both.
 *
 * A note that ends up back on its voice's own staff stops being a cross-staff note
 * altogether rather than being marked as one that happens to agree, so the file says what
 * it means and a part with one staff never carries the field at all.
 *
 * A note in an ossia stays where it is. An ossia is drawn on a short staff of its own,
 * above the staff it offers an alternative to, and that staff is the ossia's alone: there
 * is no second staff of it for a beam to cross into. See `editor/ossia.ts`.
 */
export function send(score: Score, selection: Selection, delta: number): Command | string {
  const found = selectedLocations(score, selection);
  if (found.length === 0) return "nothing is selected";

  const commands: Command[] = [];
  let single = false;
  let offered = false;
  for (const at of found) {
    const where = homeOf(score, at);
    if (where === null) continue;
    if (where.ossia) {
      offered = true;
      continue;
    }
    if (where.staves < 2) {
      single = true;
      continue;
    }
    const wanted = drawnOn(at, where.home) + delta;
    if (wanted < 0 || wanted >= where.staves) continue;
    const staff = wanted === where.home ? null : wanted;
    if (staff === at.event.staff) continue;
    commands.push({ kind: "setEventProps", eventId: at.event.id, props: { staff } });
  }

  if (commands.length === 0) {
    if (offered) return "an ossia is drawn on a staff of its own";
    return single
      ? "a part written on one staff has nowhere to send a note"
      : "there is no staff that way";
  }
  return { kind: "batch", label: "cross the staff", commands };
}

/**
 * Draw the selection on its own voice's staff again.
 *
 * The way out of a crossing that was a mistake, and the reason one does not have to be
 * undone step by step: the note goes home whichever staff it was sent to.
 */
export function putBack(score: Score, selection: Selection): Command | string {
  const found = selectedLocations(score, selection);
  if (found.length === 0) return "nothing is selected";
  const commands: Command[] = found
    .filter((at) => at.event.staff !== null)
    .map((at) => ({
      kind: "setEventProps" as const,
      eventId: at.event.id,
      props: { staff: null },
    }));
  if (commands.length === 0) return "nothing here is written across the staves";
  return { kind: "batch", label: "put back on its staff", commands };
}

/**
 * Which staff the selection is drawn on, or `null` when nothing is selected.
 *
 * The focus answers for the selection, as it does elsewhere: one number for a passage
 * whose notes are on different staves would be a number true of none of them.
 */
export function staffAt(score: Score, selection: Selection): number | null {
  const at = focusLocation(score, selection);
  if (at === null) return null;
  const where = homeOf(score, at);
  return where === null ? null : drawnOn(at, where.home);
}

/** Whether the focus of the selection has been sent to a staff that is not its voice's. */
export function isCrossed(score: Score, selection: Selection): boolean {
  const at = focusLocation(score, selection);
  return at !== null && at.event.staff !== null;
}
